import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';

export interface TooltipContent {
  title?: string;
  body?: string;
  sub?: string;
  kind?: 'neutral' | 'alert';
}

interface TooltipState extends TooltipContent {
  x: number;
  y: number;
}

interface TooltipContextValue {
  show(content: TooltipContent, x: number, y: number): void;
  hide(): void;
}

const TooltipContext = createContext<TooltipContextValue | null>(null);

export function TooltipProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<TooltipState | null>(null);

  const show = useCallback((content: TooltipContent, x: number, y: number) => {
    setState({ ...content, x, y });
  }, []);

  const hide = useCallback(() => setState(null), []);

  return (
    <TooltipContext.Provider value={{ show, hide }}>
      {children}
      {state ? <TooltipPortal state={state} /> : null}
    </TooltipContext.Provider>
  );
}

function TooltipPortal({ state }: { state: TooltipState }) {
  const offset = 12;
  return (
    <div
      className={`snap-tooltip${state.kind === 'alert' ? ' is-alert' : ''}`}
      style={{ left: state.x + offset, top: state.y + offset }}
    >
      {state.title ? (
        <div className='snap-tooltip__title'>{state.title}</div>
      ) : null}
      {state.body ? <div>{state.body}</div> : null}
      {state.sub ? <div className='snap-tooltip__sub'>{state.sub}</div> : null}
    </div>
  );
}

export function useTooltip() {
  const ctx = useContext(TooltipContext);
  if (!ctx) throw new Error('useTooltip must be used inside <TooltipProvider>');
  return ctx;
}

/**
 * Hook that returns mouse handlers wiring the given content to the global
 * tooltip. Cells/cards can spread these onto their root element.
 */
export function useTooltipHandlers(content: TooltipContent | null) {
  const { show, hide } = useTooltip();
  if (!content) return {};
  return {
    onMouseEnter: (event: React.MouseEvent) =>
      show(content, event.clientX, event.clientY),
    onMouseMove: (event: React.MouseEvent) =>
      show(content, event.clientX, event.clientY),
    onMouseLeave: hide,
  };
}
